import { useApolloClient } from '@apollo/client';
import { FlashbarProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import {
  FoodCategory,
  GetFoodCategoriesDocument,
  GetFoodCategoriesQuery,
  useUpdateFoodCategoryMutation,
} from '../../../../generated/graphql';
import { FormValues, schema } from './Create';
import Form from './Form';
import { createDDBItem } from './lib/Helper';

type EditProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedId?: string | null;
  notifyDeletedOrUpdated: (resource: FlashbarProps.MessageDefinition) => void;
  notifyInProgress: (resource?: FlashbarProps.MessageDefinition) => void;
  notifyFailed: (failedResource: FlashbarProps.MessageDefinition) => void;
};

export default function Edit({
  visible,
  selectedId,
  setVisible,
  notifyInProgress,
  notifyDeletedOrUpdated,
  notifyFailed,
}: EditProps) {
  const client = useApolloClient();
  const [updateFoodCategory, { loading: updateLoading }] =
    useUpdateFoodCategoryMutation();
  const methods = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (selectedId && selectedId.length > 0) {
      const items = client.readQuery({
        query: GetFoodCategoriesDocument,
      }) as GetFoodCategoriesQuery;
      if (
        items &&
        items.listFoodCategories &&
        items.listFoodCategories.items &&
        items.listFoodCategories.items.length > 0
      ) {
        const item = items.listFoodCategories.items.filter(
          (item) => item?.id === selectedId,
        )[0];
        if (item) {
          methods.reset({
            name: item.name ?? '',
            subtitle: item.subtitle ?? '',
            is_active: item.is_active ?? true,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, methods.reset, selectedId]);

  const onSubmit = (data: any) => {
    if (selectedId && selectedId.length > 0) {
      const entry = createDDBItem(data);
      const messageId = uuidv4();
      notifyInProgress({ id: messageId, header: 'Eintrag wird bearbeitet...' });
      updateFoodCategory({
        variables: { input: { id: selectedId, ...entry } },
        update: (cache, { data }) => {
          const addData = data?.updateFoodCategory;
          const existingData = cache.readQuery({
            query: GetFoodCategoriesDocument,
          }) as GetFoodCategoriesQuery;
          const oldData = existingData.listFoodCategories
            ?.items as FoodCategory[];
          cache.writeQuery({
            query: GetFoodCategoriesDocument,
            data: {
              listFoodCategories: {
                items: oldData.map((item) => {
                  if (item.id === selectedId) {
                    return { ...addData };
                  } else {
                    return {
                      ...item,
                    };
                  }
                }),
              },
            },
          });
        },
      })
        .then((data) => {
          const result = data.data?.updateFoodCategory;
          if (result) {
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              content: `Eintrag mit dem Namen "${result.name}" wurde bearbeitet.`,
              header: 'Gespeichert',
              dismissible: true,
            });
            setVisible(false);
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    }
  };

  const handleCancel = useCallback(() => {
    setVisible(false);
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.reset, setVisible]);

  return (
    <FormProvider {...methods}>
      <Modal
        onDismiss={handleCancel}
        visible={visible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={
                  isEmpty(methods.formState.dirtyFields) || updateLoading
                }
                formAction="submit"
                onClick={() => methods.handleSubmit(onSubmit)()}
              >
                Speichern
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={'Eintrag bearbeiten'}
      >
        <Form />
      </Modal>
    </FormProvider>
  );
}
